import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import {
  Pane,
  Card,
  Heading,
  Text,
  Button,
  Paragraph,
  Strong,
  toaster,
} from 'evergreen-ui';
import './Cart.css';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { jwtDecode } from 'jwt-decode';

// Create Cart Context
const CartContext = createContext();

// Utility function to validate JWT token
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error decoding token:', error);
    return false;
  }
};

// Helper function to calculate cart total
const calculateCartTotal = (cartItems) => {
  return cartItems.reduce((acc, item) => acc + (item.price * (item.quantity || 0)), 0);
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cartItems');
      if (savedCart) {
        const { items, expiry } = JSON.parse(savedCart);
        if (expiry && Date.now() < expiry) {
          return items || [];
        } else {
          localStorage.removeItem('cartItems');
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check token validity on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(token && isTokenValid(token));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init();
    AOS.refresh();
  }, []);

  useEffect(() => {
    // Save cart to localStorage regardless of authentication
    try {
      if (cartItems.length > 0) {
        const cartData = {
          items: cartItems,
          expiry: Date.now() + 7 * 24 * 60 * 60 * 1000,
        };
        localStorage.setItem('cartItems', JSON.stringify(cartData));
      } else {
        localStorage.removeItem('cartItems');
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [cartItems]);

  const addToCart = (product, quantity) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevItems, { ...product, quantity }];
    });
    toaster.success('Item added to cart');
    return true;
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
    toaster.success('Cart cleared');
    return true;
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        setCartItems,
        clearCart,
        isAuthenticated,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => useContext(CartContext);

// Format currency helper
const formatCurrency = (number) =>
  number.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Store Item Component
const CartStoreItem = ({ item, line, onAdd, onMinus, onRemove }) => {
  const quantity = item.quantity || 0;

  return (
    <Pane
      display="flex"
      paddingY={16}
      borderBottom={line ? '1px solid #EDEFF5' : 'none'}
    >
      <Pane flex="0 0 25%" maxWidth="25%">
        <img
          src={item.image}
          alt={item.name}
          style={{
            width: '100%',
            height: 170,
            objectFit: 'cover',
            borderRadius: 8,
          }}
        />
      </Pane>
      <Pane flex="0 0 70%" maxWidth="70%" paddingLeft={16}>
        <Pane
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={8}
        >
          <Heading size={500}>{item.name}</Heading>
          <Pane
            display="flex"
            alignItems="center"
            gap={8}
            border="1px solid #EDEFF5"
            borderRadius={6}
            padding={4}
          >
            <button
              onClick={() => onMinus(item)}
              style={{
                border: 'none',
                background: '#ffg',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <FiMinus size={24} color="#000" />
            </button>
            <Pane
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginLeft={100}
              width={40}
            >
              <Text size={600} fontWeight={600}>
                {quantity}
              </Text>
            </Pane>
            <button
              onClick={() => onAdd(item)}
              style={{
                border: 'none',
                background: '#ffg',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <FiPlus size={24} color="#000" />
            </button>
          </Pane>
        </Pane>
        <Pane display="flex" alignItems="center" marginY={4}>
          <Text size={300}>{item.code}</Text>
          {item.note && (
            <Text size={300} color="muted" marginLeft={4}>
              ({item.note})
            </Text>
          )}
        </Pane>
        <Paragraph size={300} marginY={4}>
          Color: {item.color}
        </Paragraph>
        <Paragraph size={300} marginY={4}>
          Size: {item.size}
        </Paragraph>
        <Pane
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginTop={8}
        >
          <Button
            appearance="minimal"
            iconBefore="trash"
            onClick={() => onRemove(item)}
          >
            Remove Item
          </Button>
          <Strong size={500}>{formatCurrency(item.price * quantity)}</Strong>
        </Pane>
      </Pane>
    </Pane>
  );
};

// Amount Component
const CartAmount = ({ totalOrder }) => {
  const navigate = useNavigate();

  const handleCheckout = () => {
    const token = localStorage.getItem('token');
    if (!token || !isTokenValid(token)) {
      toaster.danger('Please log in to proceed to checkout');
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  return (
    <Pane marginTop={16}>
      <Pane display="flex" justifyContent="space-between" marginBottom={8}>
        <Text size={400}>Subtotal</Text>
        <Text size={400}>{formatCurrency(totalOrder)}</Text>
      </Pane>
      <Pane borderBottom="1px solid #EDEFF5" marginY={8} />
      <Pane display="flex" justifyContent="space-between" marginY={16}>
        <Text size={400} fontWeight={500}>
          Total (Including VAT)
        </Text>
        <Strong size={400}>{formatCurrency(totalOrder)}</Strong>
      </Pane>
      <Button
        appearance="primary"
        width="100%"
        height={40}
        intent="success"
        disabled={totalOrder <= 0}
        onClick={handleCheckout}
      >
        Go To Checkout
      </Button>
    </Pane>
  );
};

// Main Cart Component
const Cart = () => {
  const { cartItems, setCartItems, addToCart } = useCart();
  const totalOrder = calculateCartTotal(cartItems);

  const handleAdd = (item) => {
    addToCart(item, 1);
  };

  const handleMinus = (item) => {
    setCartItems((prevItems) =>
      prevItems
        .map((i) =>
          i.id === item.id && i.quantity > 0
            ? { ...i, quantity: i.quantity - 1 }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const handleRemove = (item) => {
    setCartItems((prevItems) => prevItems.filter((i) => i.id !== item.id));
  };

  return (
    <Pane padding={20} maxWidth={1200} margin="auto">
      <Heading size={700} textAlign="center" marginBottom={40}>
        Shopping Cart
      </Heading>
      <Pane display="flex" gap={20} flexWrap="wrap">
        <Pane flex={2} minWidth={300}>
          <Card elevation={1} padding={16} background="white">
            <Heading size={500} marginBottom={16}>
              Cart ({cartItems.length} items)
            </Heading>
            {cartItems.length === 0 ? (
              <Pane textAlign="center" padding={16}>
                <Text>Your cart is empty</Text>
              </Pane>
            ) : (
              cartItems.map((item, index) => (
                <CartStoreItem
                  key={item.id}
                  item={item}
                  line={index < cartItems.length - 1}
                  onAdd={handleAdd}
                  onMinus={handleMinus}
                  onRemove={handleRemove}
                />
              ))
            )}
          </Card>
        </Pane>
        <Pane flex={1} minWidth={250}>
          <Card elevation={1} padding={16} background="white">
            <Heading size={500} marginBottom={16}>
              Order Summary
            </Heading>
            <CartAmount totalOrder={totalOrder} />
          </Card>
        </Pane>
      </Pane>
    </Pane>
  );
};

export default Cart;